import { ArrowLeft, CalendarClock, ChevronDown, ChevronRight, Clock, Pencil, Play, Plus, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { TermWindow } from "@/components/organisms/studio/TermWindow";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { SearchableSelect } from "@/components/organisms/studio/SearchableSelect";
import { useMetadataTypes } from "@/hooks/metadata_v1";
import { fetchRetrievalMetadataItems } from "@/hooks/metadata_v1/useRetrievalMetadataItems";
import { useOrg } from "@/hooks/org/useOrg";
import { useSkillsRequestContext } from "@/services/skills";
import { useActivityStore, selectIsIngesting, selectIsSecurityScanning, selectIsMetadataSyncing } from "@/stores/activityStore";
import { useIngestJobStore } from "@/stores/ingestJobStore";
import { useSecurityScanStore } from "@/stores/securityScanStore";
import { useRetrievalProgressStore } from "@/stores/retrievalProgressStore";
import { mapFileEdgesToOptions } from "./fileOptions";
import {
  useCreateSchedule,
  useDeleteSchedule,
  useRunScheduleNow,
  useScheduleRuns,
  useSchedules,
  useUpdateSchedule,
} from "@/hooks/studio-platform";
import type {
  ScheduleFrequency,
  SchedulePayload,
  ScheduleRecord,
  ScheduleRunRecord,
  ScheduleRunStatus,
  ScheduleTargetType,
} from "@/services/studio/types";

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const FILE_PAGE_SIZE = 100;
// The backend job isn't always created the instant runScheduleNow returns, so we
// delay the ongoing-job lookup to give it time to show up in /api/v2/jobs.
const RUN_NOW_JOB_LOOKUP_DELAY_MS = 2000;

type UIJobType = "rag_refresh" | "security_scan" | "metadata_refresh";

function defaultTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function formatDate(value?: string | null) {
  if (!value) return "Not scheduled";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function computeTargetType(uiType: UIJobType, scopeMode: string): ScheduleTargetType {
  if (uiType === "rag_refresh") return "rag_refresh";
  if (uiType === "security_scan") return "security_scan";
  if (scopeMode === "metadata_type") return "metadata_refresh_folder";
  if (scopeMode === "file") return "metadata_refresh_file";
  return "metadata_refresh_full";
}

function computeTargetId(uiType: UIJobType, scopeMode: string, scopeMetadataType: string, scopeMetadataName: string): string | undefined {
  if (uiType !== "metadata_refresh") return undefined;
  if (scopeMode === "metadata_type") return scopeMetadataType || undefined;
  if (scopeMode === "file") return scopeMetadataName || undefined;
  return undefined;
}

export function Scheduling() {
  const schedulesQ = useSchedules();
  const createSchedule = useCreateSchedule();
  const deleteSchedule = useDeleteSchedule();
  const runNow = useRunScheduleNow();
  const updateSchedule = useUpdateSchedule();
  const { selectedOrg } = useOrg();
  const { orgId, userId } = useSkillsRequestContext();
  const isRagRefreshRunning = useActivityStore(selectIsIngesting);
  const isSecurityScanRunning = useActivityStore(selectIsSecurityScanning);
  const isMetadataFullSyncRunning = useActivityStore(selectIsMetadataSyncing);
  const effectiveRetrievalId = selectedOrg?.latestMetadataRetrieval?.id;
  const metadataTypesQ = useMetadataTypes();
  const metadataTypeOptions = (metadataTypesQ.data ?? []).map((type) => ({
    value: type.metadataType,
    label: type.metadataType,
  }));
  const metadataTypePlaceholder = metadataTypesQ.isLoading
    ? "Loading metadata types…"
    : metadataTypeOptions.length === 0
    ? "No metadata types available — run a retrieval first"
    : "Select metadata type…";

  const [targetType, setTargetType] = useState<UIJobType>("rag_refresh");
  const [frequency, setFrequency] = useState<ScheduleFrequency>("daily");
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const timezone = defaultTimezone();
  const [scopeMode, setScopeMode] = useState("org");
  const [scopeMetadataType, setScopeMetadataType] = useState("");
  const [scopeMetadataName, setScopeMetadataName] = useState("");
  const [scopeFileMetadataType, setScopeFileMetadataType] = useState("");
  const [fileItems, setFileItems] = useState<{ value: string; label: string }[]>([]);
  const [fileCursor, setFileCursor] = useState<string | null>(null);
  const [fileHasMore, setFileHasMore] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileLoadingMore, setFileLoadingMore] = useState(false);
  const [screen, setScreen] = useState<"home" | "create" | "edit">("home");
  const [editingSchedule, setEditingSchedule] = useState<ScheduleRecord | null>(null);
  const [expandedScheduleId, setExpandedScheduleId] = useState<string | null>(null);

  useEffect(() => {
    setScopeMode("org");
    setScopeMetadataType("");
    setScopeMetadataName("");
    setScopeFileMetadataType("");
  }, [targetType]);

  useEffect(() => {
    setScopeMetadataName("");
    setFileItems([]);
    setFileCursor(null);
    setFileHasMore(false);
    if (scopeMode !== "file" || !scopeFileMetadataType || !effectiveRetrievalId) return;

    let cancelled = false;
    setFileLoading(true);
    fetchRetrievalMetadataItems({
      retrievalId: effectiveRetrievalId,
      metadataType: scopeFileMetadataType,
      first: FILE_PAGE_SIZE,
    })
      .then((data) => {
        if (cancelled) return;
        setFileItems(mapFileEdgesToOptions(data));
        setFileCursor(data.pageInfo.endCursor ?? null);
        setFileHasMore(data.pageInfo.hasNextPage ?? false);
      })
      .catch((err) => {
        console.error("[Scheduling] fetchRetrievalMetadataItems failed:", err);
      })
      .finally(() => {
        if (!cancelled) setFileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeMode, scopeFileMetadataType, effectiveRetrievalId]);

  const schedules = schedulesQ.data?.schedules ?? [];
  const canSave = !createSchedule.isPending;

  const resetCreateForm = () => {
    setTargetType("rag_refresh");
    setFrequency("daily");
    setTimeOfDay("09:00");
    setDayOfWeek(0);
    setDayOfMonth(1);
    setScopeMode("org");
    setScopeMetadataType("");
    setScopeMetadataName("");
    setScopeFileMetadataType("");
  };

  const loadMoreFiles = () => {
    if (!effectiveRetrievalId || !scopeFileMetadataType || !fileCursor || fileLoadingMore) return;
    const requestMetadataType = scopeFileMetadataType;
    setFileLoadingMore(true);
    fetchRetrievalMetadataItems({
      retrievalId: effectiveRetrievalId,
      metadataType: requestMetadataType,
      first: FILE_PAGE_SIZE,
      after: fileCursor,
    })
      .then((data) => {
        if (requestMetadataType !== scopeFileMetadataType) return;
        setFileItems((prev) => [...prev, ...mapFileEdgesToOptions(data)]);
        setFileCursor(data.pageInfo.endCursor ?? null);
        setFileHasMore(data.pageInfo.hasNextPage ?? false);
      })
      .catch((err) => {
        if (requestMetadataType !== scopeFileMetadataType) return;
        console.error("[Scheduling] loadMoreFiles failed:", err);
      })
      .finally(() => {
        setFileLoadingMore(false);
      });
  };

  const openCreate = () => {
    resetCreateForm();
    setScreen("create");
  };

  const openEdit = (schedule: ScheduleRecord) => {
    setTargetType(
      schedule.target_type === "rag_refresh"
        ? "rag_refresh"
        : schedule.target_type === "security_scan"
        ? "security_scan"
        : "metadata_refresh"
    );
    setFrequency(schedule.frequency);
    setTimeOfDay(schedule.time_of_day);
    setDayOfWeek(schedule.day_of_week ?? 0);
    setDayOfMonth(schedule.day_of_month ?? 1);
    setEditingSchedule(schedule);
    setScreen("edit");
  };

  const save = async () => {
    const realTargetType = computeTargetType(targetType, scopeMode);
    const realTargetId = computeTargetId(targetType, scopeMode, scopeMetadataType, scopeMetadataName);
    const payload: SchedulePayload = {
      target_type: realTargetType,
      target_id: realTargetId,
      enabled: true,
      frequency,
      time_of_day: timeOfDay,
      day_of_week: frequency === "weekly" ? dayOfWeek : null,
      day_of_month: frequency === "monthly" ? dayOfMonth : null,
      timezone,
    };
    await createSchedule.mutateAsync(payload);
    setScreen("home");
  };

  const runScheduleNow = (schedule: ScheduleRecord) => {
    runNow.mutate(schedule.id);

    if (schedule.target_type === "rag_refresh" || schedule.target_type === "security_scan") {
      if (!orgId || !userId) return;
      // The manual run only queues the job backend-side — give it a moment to
      // show up in /api/v2/jobs before we look for it to surface in ActivityDropdown.
      setTimeout(() => {
        if (schedule.target_type === "rag_refresh") {
          useIngestJobStore.getState().checkOngoingJobAndRehydrate(orgId, userId);
        } else {
          useSecurityScanStore.getState().checkOngoingJobAndRehydrate(orgId, userId);
        }
      }, RUN_NOW_JOB_LOOKUP_DELAY_MS);
      return;
    }

    if (schedule.target_type === "metadata_refresh_full") {
      if (!effectiveRetrievalId) return;
      // The org's retrieval id is stable — the manual run kicks off a new full
      // refresh under that same id, so we just reconnect the existing SSE stream
      // (RetrievalProgressRefreshManager mirrors it into ActivityDropdown).
      setTimeout(() => {
        useRetrievalProgressStore.getState().connect(effectiveRetrievalId);
      }, RUN_NOW_JOB_LOOKUP_DELAY_MS);
    }
  };

  const update = async () => {
    if (!editingSchedule) return;
    const payload: SchedulePayload = {
      target_type: editingSchedule.target_type,
      enabled: editingSchedule.enabled,
      frequency,
      time_of_day: timeOfDay,
      day_of_week: frequency === "weekly" ? dayOfWeek : null,
      day_of_month: frequency === "monthly" ? dayOfMonth : null,
      timezone,
    };
    await updateSchedule.mutateAsync({ scheduleId: editingSchedule.id, ...payload });
    setEditingSchedule(null);
    setScreen("home");
  };

  if (screen === "create") {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          icon={CalendarClock}
          title="New Schedule"
          description="Pick a job type and define the org scope"
          actions={
            <Button variant="outline" size="sm" onClick={() => setScreen("home")}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          }
        />

        <div className="space-y-5">
          <Field label="Run type">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Segment active={targetType === "rag_refresh"} onClick={() => setTargetType("rag_refresh")} label="RAG Re-index" />
              <Segment active={targetType === "security_scan"} onClick={() => setTargetType("security_scan")} label="Security Scan" />
              <Segment active={targetType === "metadata_refresh"} onClick={() => setTargetType("metadata_refresh")} label="Metadata Refresh" />
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            <Field label="Frequency">
              <select value={frequency} onChange={(event) => setFrequency(event.target.value as ScheduleFrequency)} className="h-10 w-full rounded-md border bg-card px-3 text-sm font-mono">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field label="Time">
              <Input type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} />
            </Field>
            {frequency === "weekly" ? (
              <Field label="Day">
                <select value={dayOfWeek} onChange={(event) => setDayOfWeek(Number(event.target.value))} className="h-10 w-full rounded-md border bg-card px-3 text-sm font-mono">
                  {DAY_LABELS.map((label, idx) => <option key={label} value={idx}>{label}</option>)}
                </select>
              </Field>
            ) : null}
            {frequency === "monthly" ? (
              <Field label="Day">
                <Input type="number" min={1} max={31} value={dayOfMonth} onChange={(event) => setDayOfMonth(Number(event.target.value))} />
              </Field>
            ) : null}
          </div>

          {targetType === "metadata_refresh" && (
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Scope">
                <select value={scopeMode} onChange={(event) => setScopeMode(event.target.value)} className="h-10 w-full rounded-md border bg-card px-3 text-sm font-mono">
                  <option value="org">Org wide</option>
                  <option value="metadata_type">Metadata type</option>
                  <option value="file">File / metadata name</option>
                </select>
              </Field>
              {scopeMode === "metadata_type" && (
                <Field label="Metadata type">
                  <SearchableSelect
                    value={scopeMetadataType}
                    onChange={setScopeMetadataType}
                    options={metadataTypeOptions}
                    placeholder={metadataTypePlaceholder}
                    disabled={metadataTypeOptions.length === 0}
                  />
                </Field>
              )}
              {scopeMode === "file" && (
                <>
                  <Field label="Metadata type">
                    <SearchableSelect
                      value={scopeFileMetadataType}
                      onChange={setScopeFileMetadataType}
                      options={metadataTypeOptions}
                      placeholder={metadataTypePlaceholder}
                      disabled={metadataTypeOptions.length === 0}
                    />
                  </Field>
                  <Field label="File">
                    <SearchableSelect
                      value={scopeMetadataName}
                      onChange={setScopeMetadataName}
                      options={fileItems}
                      placeholder={
                        !scopeFileMetadataType
                          ? "Select metadata type first"
                          : fileLoading
                          ? "Loading files…"
                          : fileItems.length === 0
                          ? "No files found for this type"
                          : "Select file…"
                      }
                      disabled={!scopeFileMetadataType || fileLoading || fileItems.length === 0}
                      hasMore={fileHasMore}
                      onLoadMore={loadMoreFiles}
                      loadingMore={fileLoadingMore}
                    />
                  </Field>
                </>
              )}
            </div>
          )}

          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs font-mono text-muted-foreground">
            {targetType === "rag_refresh" && "Re-indexes all org metadata into the RAG knowledge base. No additional inputs needed."}
            {targetType === "security_scan" && "Runs a full security scan across all org metadata. No additional inputs needed."}
            {targetType === "metadata_refresh" && "Refreshes org metadata from Salesforce. Use scope to target a specific type or file."}
          </div>

          {createSchedule.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm font-mono text-destructive">
              {(createSchedule.error as Error)?.message ?? "Could not save schedule."}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="charcoal" size="lg" onClick={save} disabled={!canSave}>
              <Plus className="h-4 w-4" />
              Save Schedule
            </Button>
            <Button variant="outline" size="lg" onClick={() => setScreen("home")}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "edit") {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          icon={CalendarClock}
          title="Edit Schedule"
          description="Update the schedule's timing"
          actions={
            <Button variant="outline" size="sm" onClick={() => setScreen("home")}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          }
        />

        <div className="space-y-5">
          <Field label="Run type">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm font-mono text-muted-foreground">
              {editingSchedule?.target_type}
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            <Field label="Frequency">
              <select value={frequency} onChange={(event) => setFrequency(event.target.value as ScheduleFrequency)} className="h-10 w-full rounded-md border bg-card px-3 text-sm font-mono">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field label="Time">
              <Input type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} />
            </Field>
            {frequency === "weekly" ? (
              <Field label="Day">
                <select value={dayOfWeek} onChange={(event) => setDayOfWeek(Number(event.target.value))} className="h-10 w-full rounded-md border bg-card px-3 text-sm font-mono">
                  {DAY_LABELS.map((label, idx) => <option key={label} value={idx}>{label}</option>)}
                </select>
              </Field>
            ) : null}
            {frequency === "monthly" ? (
              <Field label="Day">
                <Input type="number" min={1} max={31} value={dayOfMonth} onChange={(event) => setDayOfMonth(Number(event.target.value))} />
              </Field>
            ) : null}
          </div>

          {updateSchedule.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm font-mono text-destructive">
              {(updateSchedule.error as Error)?.message ?? "Could not update schedule."}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="charcoal" size="lg" onClick={update} disabled={updateSchedule.isPending}>
              Save Changes
            </Button>
            <Button variant="outline" size="lg" onClick={() => setScreen("home")}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        icon={CalendarClock}
        title="Scheduling"
        description="Manage recurring scheduled jobs for the selected org"
        actions={
          <button type="button" className="studio-button studio-button-primary" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New schedule
          </button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]">
        <TermWindow title="schedules.list" status={schedulesQ.isLoading ? "loading" : "ready"}>
          {schedulesQ.isError ? (
            <p className="text-sm font-mono text-destructive">{(schedulesQ.error as Error)?.message ?? "Could not load schedules."}</p>
          ) : schedules.length ? (
            <div className="grid gap-3">
              {schedules.map((schedule) => (
                <ScheduleRow
                  key={schedule.id}
                  schedule={schedule}
                  onRun={() => runScheduleNow(schedule)}
                  onEdit={() => openEdit(schedule)}
                  onDelete={() => deleteSchedule.mutate(schedule.id)}
                  busy={runNow.isPending || deleteSchedule.isPending}
                  runDisabled={
                    (schedule.target_type === "rag_refresh" && isRagRefreshRunning) ||
                    (schedule.target_type === "security_scan" && isSecurityScanRunning) ||
                    (schedule.target_type === "metadata_refresh_full" && isMetadataFullSyncRunning)
                  }
                  expanded={expandedScheduleId === schedule.id}
                  onToggleExpand={() =>
                    setExpandedScheduleId((prev) => (prev === schedule.id ? null : schedule.id))
                  }
                />
              ))}
            </div>
          ) : (
            <p className="text-sm font-mono text-muted-foreground">No scheduled jobs yet.</p>
          )}
        </TermWindow>

        <TermWindow title="schedule.status" status={`${schedules.length} configured`}>
          <div className="space-y-3">
            <Metric icon={<CalendarClock className="h-4 w-4" />} label="Active schedules" value={String(schedules.filter((s) => s.enabled).length)} />
            <Metric icon={<Clock className="h-4 w-4" />} label="Next run" value={formatDate(schedules.find((s) => s.next_run_at)?.next_run_at)} />
          </div>
        </TermWindow>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-mono text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function Segment({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-md border px-3 py-2 text-sm font-mono transition-colors",
        active ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:bg-muted",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-sm">
      <span className="inline-flex min-w-0 items-center gap-2 font-mono text-muted-foreground">{icon}{label}</span>
      <span className="truncate font-mono text-xs">{value}</span>
    </div>
  );
}

function scheduleRunBadgeVariant(status: ScheduleRunStatus): "success" | "destructive" | "warm" | "default" {
  switch (status) {
    case "success":
      return "success";
    case "error":
      return "destructive";
    case "timeout":
      return "warm";
    case "running":
      return "default";
  }
}

function ScheduleRow({
  schedule,
  onRun,
  onEdit,
  onDelete,
  busy,
  runDisabled,
  expanded,
  onToggleExpand,
}: {
  schedule: ScheduleRecord;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
  busy: boolean;
  runDisabled?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{schedule.target_type}</Badge>
            <span className="truncate font-mono text-sm font-semibold">{schedule.target_name || schedule.target_id}</span>
            {schedule.last_run_status ? <Badge variant={schedule.last_run_status === "error" ? "destructive" : "secondary"}>{schedule.last_run_status}</Badge> : null}
          </div>
          <div className="grid gap-1 text-xs font-mono text-muted-foreground md:grid-cols-2">
            <span>{schedule.frequency} at {schedule.time_of_day} {schedule.timezone}</span>
            <span>Next: {formatDate(schedule.next_run_at)}</span>
          </div>
          {schedule.last_run_error ? <p className="mt-2 text-xs font-mono text-destructive">{schedule.last_run_error}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onToggleExpand}>
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            History
          </Button>
          <Button variant="outline" size="sm" onClick={onRun} disabled={busy || runDisabled}>
            <Play className="h-3.5 w-3.5" />
            {runDisabled ? "Running…" : "Run Now"}
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit} disabled={busy}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={onDelete} disabled={busy}>
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>
      {expanded ? <ScheduleRunHistory scheduleId={schedule.id} /> : null}
    </div>
  );
}

function ScheduleRunHistory({ scheduleId }: { scheduleId: string }) {
  const runsQ = useScheduleRuns(scheduleId);
  const runs = runsQ.data?.runs ?? [];

  return (
    <div className="mt-3 border-t pt-3">
      {runsQ.isLoading ? (
        <p className="text-xs font-mono text-muted-foreground">Loading run history…</p>
      ) : runsQ.isError ? (
        <p className="text-xs font-mono text-destructive">{(runsQ.error as Error)?.message ?? "Could not load run history."}</p>
      ) : runs.length === 0 ? (
        <p className="text-xs font-mono text-muted-foreground">No runs yet.</p>
      ) : (
        <div className="grid gap-2">
          {runs.map((run: ScheduleRunRecord) => (
            <div key={run.id} className="grid gap-1 rounded-md border bg-muted/20 p-2 text-xs font-mono md:grid-cols-[auto_1fr_1fr] md:items-center">
              <Badge variant={scheduleRunBadgeVariant(run.status)}>{run.status}</Badge>
              <span className="text-muted-foreground">Started: {formatDate(run.started_at)}</span>
              <span className="text-muted-foreground">{run.completed_at ? `Completed: ${formatDate(run.completed_at)}` : "In progress"}</span>
              {run.error_message ? <p className="text-destructive md:col-span-3">{run.error_message}</p> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
